import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  IconButton,
  Slider,
  TextField,
  Chip,
  Stack,
  Collapse
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterAlt as FilterAltIcon
} from "@mui/icons-material";

const FilterSection = ({ title, children, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 1,
          '&:hover': { bgcolor: 'action.hover' }
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pl: 1, pr: 1, pt: 1 }}>
          {children}
        </Box>
      </Collapse>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
};

const FilterSidebar = ({ 
  open, 
  onClose, 
  onApplyFilters, 
  availableCategories = [], 
  availableBrands = [],
  initialFilters = {}
}) => {
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [searchBrand, setSearchBrand] = useState("");
  const [sortBy, setSortBy] = useState(initialFilters.sortBy || null);
  const [sortOrder, setSortOrder] = useState(initialFilters.sortOrder || null);

  // Initialize filters from props
  useEffect(() => {
    if (initialFilters) {
      if (initialFilters.brands && Array.isArray(initialFilters.brands)) {
        setSelectedBrands(initialFilters.brands);
      } else {
        setSelectedBrands([]);
      }

      if (initialFilters.minPrice !== undefined || initialFilters.maxPrice !== undefined) {
        setPriceRange([
          initialFilters.minPrice || 0,
          initialFilters.maxPrice || 100000
        ]);
      } else {
        setPriceRange([0, 100000]);
      }

      if (initialFilters.sortBy) {
        setSortBy(initialFilters.sortBy);
      } else {
        setSortBy(null);
      }
      
      if (initialFilters.sortOrder) {
        setSortOrder(initialFilters.sortOrder);
      } else {
        setSortOrder(null);
      }
    }
  }, [initialFilters]);

  // Filter brands based on search
  const filteredBrands = availableBrands.filter(brand =>
    brand.toLowerCase().includes(searchBrand.toLowerCase())
  );

  const handleBrandToggle = (brand) => {
    setSelectedBrands(prev =>
      prev.includes(brand)
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  };

  const handleSelectAllBrands = () => {
    if (selectedBrands.length === filteredBrands.length) {
      setSelectedBrands([]);
    } else {
      setSelectedBrands([...filteredBrands]);
    }
  };

  const handleSortChange = (field, order) => {
    // If the same sort option is clicked again, clear the sort
    if (sortBy === field && sortOrder === order) {
      setSortBy(null);
      setSortOrder(null);
    } else {
      setSortBy(field);
      setSortOrder(order);
    }
  };

  const handleApplyFilters = () => {
    const filters = {
      brands: selectedBrands,
      minPrice: priceRange[0],
      maxPrice: priceRange[1],
      sortBy: sortBy,
      sortOrder: sortOrder
    };
    onApplyFilters(filters);
    onClose();
  };

  const handleClearFilters = () => {
    setSelectedBrands([]);
    setPriceRange([0, 100000]);
    setSearchBrand("");
    setSortBy(null);
    setSortOrder(null);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const activeFilterCount = () => {
    let count = 0;
    if (selectedBrands.length > 0) count++;
    if (priceRange[0] > 0 || priceRange[1] < 100000) count++;
    if (sortBy !== null && sortOrder !== null) count++;
    return count;
  };

  const isSortActive = (field, order) => {
    return sortBy === field && sortOrder === order;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          p: 2
        }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Filters
          {activeFilterCount() > 0 && (
            <Chip
              label={activeFilterCount()}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Filter Content */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Sort Options */}
        <FilterSection title="Sort By" defaultExpanded={true}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={isSortActive("price", "ASC") ? "contained" : "outlined"}
                size="small"
                onClick={() => handleSortChange("price", "ASC")}
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.75rem', 
                  flex: 1,
                  ...(isSortActive("price", "ASC") && {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    }
                  })
                }}
              >
                Price: Low to High
              </Button>
              <Button
                variant={isSortActive("price", "DESC") ? "contained" : "outlined"}
                size="small"
                onClick={() => handleSortChange("price", "DESC")}
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.75rem', 
                  flex: 1,
                  ...(isSortActive("price", "DESC") && {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    }
                  })
                }}
              >
                Price: High to Low
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={isSortActive("title", "ASC") ? "contained" : "outlined"}
                size="small"
                onClick={() => handleSortChange("title", "ASC")}
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.75rem', 
                  flex: 1,
                  ...(isSortActive("title", "ASC") && {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    }
                  })
                }}
              >
                Title: A to Z
              </Button>
              <Button
                variant={isSortActive("title", "DESC") ? "contained" : "outlined"}
                size="small"
                onClick={() => handleSortChange("title", "DESC")}
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.75rem', 
                  flex: 1,
                  ...(isSortActive("title", "DESC") && {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    }
                  })
                }}
              >
                Title: Z to A
              </Button>
            </Box>
          </Box>
        </FilterSection>

        {/* Price Range Filter */}
        <FilterSection title="Price Range">
          <Box sx={{ px: 1 }}>
            <Slider
              value={priceRange}
              onChange={(e, newValue) => setPriceRange(newValue)}
              valueLabelDisplay="auto"
              min={0}
              max={100000}
              step={1000}
              valueLabelFormat={(value) => formatPrice(value)}
              sx={{ mt: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {formatPrice(priceRange[0])}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatPrice(priceRange[1])}
              </Typography>
            </Box>
          </Box>
        </FilterSection>

        {/* Brands Filter with Multi-select */}
        {availableBrands.length > 0 && (
          <FilterSection title="Brands">
            <Box sx={{ mb: 1 }}>
              <TextField
                size="small"
                placeholder="Search brands..."
                value={searchBrand}
                onChange={(e) => setSearchBrand(e.target.value)}
                fullWidth
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Button
                  size="small"
                  onClick={handleSelectAllBrands}
                  sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                >
                  {selectedBrands.length === filteredBrands.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {selectedBrands.length} selected
                </Typography>
              </Box>
            </Box>
            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {filteredBrands.map((brand) => (
                <FormControlLabel
                  key={brand}
                  control={
                    <Checkbox
                      checked={selectedBrands.includes(brand)}
                      onChange={() => handleBrandToggle(brand)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {brand}
                    </Typography>
                  }
                  sx={{ ml: -0.5, mb: 0.5 }}
                />
              ))}
              {filteredBrands.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
                  No brands found
                </Typography>
              )}
            </Box>
          </FilterSection>
        )}
      </Box>

      {/* Footer Buttons */}
      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleClearFilters}
            sx={{ textTransform: 'none' }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleApplyFilters}
            startIcon={<FilterAltIcon />}
            sx={{ textTransform: 'none' }}
          >
            Apply Filters
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default FilterSidebar;